import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Ban, ExternalLink, Eye, Inbox, Layers, Loader2, MoreHorizontal, RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
    EmptyState,
    PillTabs,
    RelativeTime,
    SectionCard,
    TablePagination,
    formatNumber,
} from '../../../components/design-system/admin';
import { JobStatusBadge } from './JobStatusBadge';
import type { QueueJob, QueueType, StatusFilter } from './queue-monitor.types';

const PAGE_SIZES = [10, 25, 50];

interface QueueJobsTableProps {
    /** Already filtered by `statusFilter`. */
    jobs: QueueJob[];
    queue: QueueType;
    statusFilter: StatusFilter;
    onStatusFilterChange: (status: StatusFilter) => void;
    /** Per-status totals for the tabs (from the queue summary, not just the jobs listed). */
    counts: Record<StatusFilter, number>;
    replayingJobId: string | null;
    cancellingBatch: string | null;
    onViewDetails: (jobId: string) => void;
    onReplay: (jobId: string) => void;
    onCancelBatch: (batchId: string) => void;
}

/** A result that lives elsewhere, opened in a new tab (as before). */
const ResultLink = ({ href, icon, children }: { href: string; icon: ReactNode; children: ReactNode }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs text-foreground underline-offset-4 hover:underline [&_svg]:size-3.5 [&_svg]:text-muted-foreground"
    >
        {icon}
        {children}
    </a>
);

/** The most recent jobs of the queue (the API returns up to 100), filtered by status. */
export function QueueJobsTable({
    jobs,
    queue,
    statusFilter,
    onStatusFilterChange,
    counts,
    replayingJobId,
    cancellingBatch,
    onViewDetails,
    onReplay,
    onCancelBatch,
}: QueueJobsTableProps) {
    const { t } = useTranslation();
    const seamless = queue === 'seamless';
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);

    useEffect(() => setPage(0), [statusFilter, queue]);

    // The list refreshes every 30 s and can shrink: never sit past its end.
    const lastPage = Math.max(0, Math.ceil(jobs.length / pageSize) - 1);
    const current = Math.min(page, lastPage);
    const rows = jobs.slice(current * pageSize, (current + 1) * pageSize);

    const result = (job: QueueJob) => {
        const done = job.status === 'completed';
        const links: ReactNode[] = [];
        if (seamless && done && job.resultUrl) {
            links.push(
                <ResultLink key="texture" href={job.resultUrl} icon={<Layers />}>
                    {t('ADMIN_QUEUE.JOBS.VIEW_TEXTURE')}
                </ResultLink>,
            );
        }
        // Products live in the vendor portal; from here the useful stop is the vendor.
        if (done && job.resultProductId && job.vendorId && (!seamless || job.isBulkSeamless)) {
            links.push(
                <ResultLink key="vendor" href={`/vendors/${job.vendorId}/edit`} icon={<ExternalLink />}>
                    {t('ADMIN_QUEUE.JOBS.VIEW_VENDOR')}
                </ResultLink>,
            );
        }
        return links.length ? <div className="flex flex-wrap gap-3">{links}</div> : <span className="text-muted-foreground">—</span>;
    };

    return (
        <SectionCard
            title={t('ADMIN_QUEUE.JOBS.TITLE')}
            description={t(statusFilter === 'stuck' ? 'ADMIN_QUEUE.JOBS.COUNT_STUCK' : 'ADMIN_QUEUE.JOBS.COUNT', { count: jobs.length })}
            flush
            testId="queue-jobs"
        >
            <div className="px-4 pb-3">
                <PillTabs<StatusFilter>
                    value={statusFilter}
                    onChange={onStatusFilterChange}
                    ariaLabel={t('ADMIN_QUEUE.JOBS.STATUS_FILTER')}
                    options={[
                        { value: 'all', label: t('ADMIN_SHELL.COMMON.ALL'), count: counts.all },
                        { value: 'active', label: t('ADMIN_QUEUE.FILTER.ACTIVE'), count: counts.active },
                        { value: 'waiting', label: t('ADMIN_QUEUE.FILTER.WAITING'), count: counts.waiting },
                        { value: 'completed', label: t('ADMIN_QUEUE.FILTER.COMPLETED'), count: counts.completed },
                        { value: 'failed', label: t('ADMIN_QUEUE.FILTER.FAILED'), count: counts.failed },
                        // Only when there is something stuck (or the link asked for it).
                        ...(counts.stuck > 0 || statusFilter === 'stuck'
                            ? [{ value: 'stuck' as StatusFilter, label: t('ADMIN_QUEUE.FILTER.STUCK'), count: counts.stuck }]
                            : []),
                    ]}
                />
            </div>
            {jobs.length === 0 ? (
                <EmptyState
                    icon={<Inbox />}
                    title={statusFilter === 'all' ? t('ADMIN_QUEUE.JOBS.EMPTY') : t('ADMIN_QUEUE.JOBS.EMPTY_STATUS')}
                    className="border-t"
                />
            ) : (
                <>
                    <div className="border-t">
                        <Table>
                            <TableHeader>
                                <TableRow className="hover:bg-transparent">
                                    <TableHead className="ps-4">{t('ADMIN_QUEUE.JOBS.COL_NAME')}</TableHead>
                                    <TableHead>{seamless ? t('ADMIN_QUEUE.JOBS.COL_PATTERN') : t('ADMIN_QUEUE.JOBS.COL_OPERATION')}</TableHead>
                                    <TableHead>{t('ADMIN_QUEUE.JOBS.COL_STATUS')}</TableHead>
                                    <TableHead>{seamless ? t('ADMIN_QUEUE.JOBS.COL_RESULT') : t('ADMIN_QUEUE.JOBS.COL_PRODUCT')}</TableHead>
                                    <TableHead className="text-end">{t('ADMIN_QUEUE.JOBS.COL_ATTEMPTS')}</TableHead>
                                    <TableHead>{t('ADMIN_QUEUE.JOBS.COL_CREATED')}</TableHead>
                                    <TableHead className="w-12 pe-4">
                                        <span className="sr-only">{t('ADMIN_SHELL.COMMON.ACTIONS')}</span>
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((job) => {
                                    const error = job.status === 'failed' ? job.errorDetails?.userMessage || job.error : undefined;
                                    const replaying = replayingJobId === job._id;
                                    const cancellable = !!job.batchId && job.status !== 'completed' && job.status !== 'failed';
                                    const cancelling = !!job.batchId && cancellingBatch === job.batchId;
                                    return (
                                        <TableRow key={job._id} data-testid="queue-job-row">
                                            <TableCell className="max-w-[320px] ps-4">
                                                <button
                                                    type="button"
                                                    onClick={() => onViewDetails(job._id)}
                                                    className="block max-w-full truncate text-start text-sm hover:underline"
                                                    title={job.jobName}
                                                >
                                                    {job.jobName}
                                                </button>
                                                {error && (
                                                    <p className="max-w-full truncate text-xs text-destructive" title={error}>
                                                        {error}
                                                    </p>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground capitalize">{job.operation}</TableCell>
                                            <TableCell>
                                                <JobStatusBadge status={job.status} />
                                            </TableCell>
                                            <TableCell>{result(job)}</TableCell>
                                            <TableCell className="text-end text-muted-foreground tabular-nums">
                                                {formatNumber(job.attempts ?? 0)}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">
                                                <RelativeTime value={job.createdAt} />
                                            </TableCell>
                                            <TableCell className="pe-4 text-end">
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon-sm" aria-label={t('ADMIN_QUEUE.JOBS.ROW_ACTIONS')}>
                                                            {replaying || cancelling ? <Loader2 className="animate-spin" /> : <MoreHorizontal />}
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="w-auto">
                                                        <DropdownMenuItem onSelect={() => onViewDetails(job._id)}>
                                                            <Eye />
                                                            {t('ADMIN_QUEUE.JOBS.VIEW_DETAILS')}
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem disabled={replaying} onSelect={() => onReplay(job._id)}>
                                                            <RotateCcw />
                                                            {t('ADMIN_QUEUE.JOBS.REPLAY')}
                                                        </DropdownMenuItem>
                                                        {cancellable && (
                                                            <>
                                                                <DropdownMenuSeparator />
                                                                <DropdownMenuItem
                                                                    variant="destructive"
                                                                    disabled={cancelling}
                                                                    onSelect={() => onCancelBatch(job.batchId!)}
                                                                >
                                                                    <Ban />
                                                                    {t('ADMIN_QUEUE.BATCHES.CANCEL')}
                                                                </DropdownMenuItem>
                                                            </>
                                                        )}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                    <div className="border-t">
                        <TablePagination
                            page={current}
                            pageSize={pageSize}
                            total={jobs.length}
                            onPageChange={setPage}
                            onPageSizeChange={(size) => {
                                setPageSize(size);
                                setPage(0);
                            }}
                            pageSizes={PAGE_SIZES}
                        />
                    </div>
                </>
            )}
        </SectionCard>
    );
}
